import { useState } from "react";
import { Download, Mail, FileText, CheckCircle2, AlertTriangle, Loader2, Send, Sparkles, Pencil, Save, X, Plus, Trash2, Lightbulb, ExternalLink, ChevronDown, Share2, StickyNote } from "lucide-react";
import { toast } from "sonner";
import api, { downloadFile } from "../../lib/api";
import AtsGauge from "./AtsGauge";
import ShareWinModal from "./ShareWinModal";
import SkillGapsBox, { normGaps } from "./SkillGapsBox";
import CompatibilityCard from "./CompatibilityCard";
import ToolsMatch from "../studio/ToolsMatch";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "../ui/alert-dialog";

export default function StudioResult({ app, onEmailed, onUpdated }) {
  const [dl, setDl] = useState(null);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(app.email_sent);
  const [cv, setCv] = useState(app.tailored_cv || {});
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [coaching, setCoaching] = useState(app.gap_coaching || null);
  const [loadingCoach, setLoadingCoach] = useState(false);
  const [template, setTemplate] = useState("classic");
  const [jdOpen, setJdOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [notes, setNotes] = useState({
    recruiter_name: app.recruiter_name || "",
    follow_up_date: app.follow_up_date || "",
    notes: app.notes || "",
  });
  const [savingNotes, setSavingNotes] = useState(false);
  const [gaps, setGaps] = useState(normGaps(app.skill_gaps) || null);
  const [atsScore, setAtsScore] = useState(app.ats_score);
  const [compat, setCompat] = useState(app.compatibility || null);
  const [confirmSend, setConfirmSend] = useState(false);

  const applyGaps = (data) => {
    if (data.skill_gaps) {
      setGaps(data.skill_gaps);
      app.skill_gaps = data.skill_gaps;
    }
    if (data.tailored_cv) {
      setCv(data.tailored_cv);
      app.tailored_cv = data.tailored_cv;
    }
    if (data.compatibility) {
      setCompat(data.compatibility);
      app.compatibility = data.compatibility;
    }
    setAtsScore(data.ats_score);
    app.ats_score = data.ats_score;
    onUpdated?.();
  };

  const coachGaps = () => {
    if (!coaching) loadCoaching();
    document.querySelector('[data-testid="gap-coaching-card"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const unconfirmed = (gaps?.items || []).filter((i) => i.status === "auto_added");
  const confirmedSkills = (gaps?.items || []).filter((i) => i.status === "confirmed").map((i) => i.skill);
  const reviewGaps = () => {
    setTimeout(() => document.getElementById("evidence-checker")?.scrollIntoView({ behavior: "smooth", block: "center" }), 150);
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.patch(`/applications/${app.id}/notes`, notes);
      app.recruiter_name = notes.recruiter_name;
      app.follow_up_date = notes.follow_up_date;
      app.notes = notes.notes;
      onUpdated?.();
      toast.success("Notes saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save notes");
    } finally {
      setSavingNotes(false);
    }
  };

  const gapCount = (app.recommendations || []).filter((r) => /^gap:/i.test(r)).length + (app.missing_keywords || []).length;

  const doDownload = async (kind) => {
    setDl(kind);
    const map = {
      cv: [`/applications/${app.id}/cv.pdf?template=${template}`, `CV_${app.company || "application"}.pdf`],
      cl: [`/applications/${app.id}/cover-letter.pdf`, `CoverLetter_${app.company || "application"}.pdf`],
      "cv-docx": [`/applications/${app.id}/cv.docx`, `CV_${app.company || "application"}.docx`],
      "cl-docx": [`/applications/${app.id}/cover-letter.docx`, `CoverLetter_${app.company || "application"}.docx`],
    };
    try {
      await downloadFile(map[kind][0], map[kind][1]);
    } catch {
      toast.error("Download failed");
    } finally {
      setDl(null);
    }
  };

  const startEdit = () => { setDraft(JSON.parse(JSON.stringify(cv))); setEditing(true); };
  const cancelEdit = () => { setEditing(false); setDraft(null); };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.patch(`/applications/${app.id}/cv`, { tailored_cv: draft });
      setCv(draft);
      app.tailored_cv = draft;
      onUpdated?.();
      setEditing(false);
      setDraft(null);
      toast.success("CV updated — downloads now use your edits");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save your edits");
    } finally {
      setSaving(false);
    }
  };

  const setBullet = (ei, bi, val) => setDraft((d) => {
    const exp = [...d.experience];
    const bullets = [...(exp[ei].bullets || [])];
    bullets[bi] = val;
    exp[ei] = { ...exp[ei], bullets };
    return { ...d, experience: exp };
  });
  const addBullet = (ei) => setDraft((d) => {
    const exp = [...d.experience];
    exp[ei] = { ...exp[ei], bullets: [...(exp[ei].bullets || []), ""] };
    return { ...d, experience: exp };
  });
  const removeBullet = (ei, bi) => setDraft((d) => {
    const exp = [...d.experience];
    exp[ei] = { ...exp[ei], bullets: (exp[ei].bullets || []).filter((_, j) => j !== bi) };
    return { ...d, experience: exp };
  });

  const loadCoaching = async () => {
    setLoadingCoach(true);
    try {
      const { data } = await api.post(`/applications/${app.id}/gap-coaching`);
      setCoaching(data.coaching || []);
      if (!data.coaching?.length) toast.info("No gaps to coach — you're looking strong!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load coaching just now");
    } finally {
      setLoadingCoach(false);
    }
  };

  const sendEmail = async (confirmed = false) => {
    if (!email.trim()) return toast.error("Enter a recruiter email");
    if (unconfirmed.length && confirmed !== true) return setConfirmSend(true);
    setSending(true);
    try {
      await api.post(`/applications/${app.id}/email`, { recruiter_email: email.trim() });
      const listed = [...confirmedSkills, ...unconfirmed.map((i) => i.skill)];
      toast.success(`Application emailed to ${email.trim()}`, listed.length ? {
        description: `Your CV included ${listed.length} skill${listed.length === 1 ? "" : "s"} you added for this job (${listed.join(", ")}). Be ready to give an example of each.`,
        duration: 8000,
      } : undefined);
      setSent(true);
      onEmailed?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  const view = editing ? draft : cv;

  return (
    <div className="af-fade-up space-y-6 pb-20 sm:pb-0">
      {gaps?.items?.length > 0 && <SkillGapsBox app={app} gaps={gaps} supported={gaps.supported?.length ? gaps.supported : app.matched_keywords} onApplied={applyGaps} onCoach={coachGaps} />}

      {/* Score + summary */}
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="rounded-xl border border-line bg-surface p-6 flex flex-col items-center justify-center">
          <AtsGauge score={atsScore} label={compat ? "JOB MATCH" : "ATS MATCH"} />
          <div className="mt-3 text-xs text-faint font-mono2">
            {compat ? "before tailoring (estimate)" : "original CV was"} <span className="text-warning">{app.baseline_score}%</span>
          </div>
          <button
            data-testid="share-win-button"
            onClick={() => setShareOpen(true)}
            className="mt-4 flex items-center gap-2 px-4 py-2 rounded-xl border border-brand/40 text-brand text-sm font-medium hover:bg-brand-soft transition-colors duration-200"
          >
            <Share2 className="w-4 h-4" /> Share this win
          </button>
        </div>
        <div className="lg:col-span-2 rounded-xl border border-line bg-surface p-6">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-brand" />
            <span className="font-mono2 text-xs uppercase tracking-widest text-brand">Match Summary</span>
          </div>
          <h2 className="font-display text-xl font-bold">{app.role || "Tailored role"} <span className="text-faint font-normal">· {app.company || "Company"}</span></h2>
          <p className="text-sm text-body mt-2 leading-relaxed">{app.match_summary}</p>
        </div>
      </div>

      {compat && <CompatibilityCard app={app} compat={compat} pending={(gaps?.items || []).filter((i) => i.status === "needs_info" || i.status === "auto_added").length} onChange={applyGaps} />}

      {/* Job description this application targets */}
      {(app.jd_text || app.jd_url) && (
        <div className="rounded-xl border border-line bg-surface p-5" data-testid="jd-section">
          <button
            data-testid="jd-toggle"
            onClick={() => setJdOpen((v) => !v)}
            className="w-full flex items-center justify-between gap-3"
          >
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-brand" />
              <h3 className="font-semibold">Job description you applied to</h3>
              {app.jd_url && (
                <a href={app.jd_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-faint hover:text-brand inline-flex items-center gap-1 text-xs">
                  <ExternalLink className="w-3.5 h-3.5" /> source
                </a>
              )}
            </div>
            <ChevronDown className={`w-4 h-4 text-faint transition-transform ${jdOpen ? "rotate-180" : ""}`} />
          </button>
          {jdOpen && app.jd_text && (
            <div className="mt-4 rounded-lg bg-canvas border border-line p-4 max-h-72 overflow-y-auto">
              <p className="text-sm text-body whitespace-pre-wrap leading-relaxed">{app.jd_text}</p>
            </div>
          )}
          {!jdOpen && (
            <p className="mt-3 text-sm text-faint line-clamp-2">{(app.jd_text || "").slice(0, 220)}{(app.jd_text || "").length > 220 ? "…" : ""}</p>
          )}
        </div>
      )}

      {/* Keyword matrix */}
      <div className="grid md:grid-cols-2 gap-5">
        <div className="rounded-xl border border-line bg-surface p-5" data-testid="keyword-match-list">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-4 h-4 text-brand" />
            <h3 className="font-semibold">Matched keywords</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {(app.matched_keywords || []).map((k) => (
              <span key={k} className="font-mono2 text-xs px-2.5 py-1 rounded-full border border-brand/30 bg-brand-soft text-brand">{k}</span>
            ))}
            {(app.matched_keywords || []).length === 0 && <span className="text-sm text-faint">—</span>}
          </div>
        </div>
        <div className="rounded-xl border border-line bg-surface p-5" data-testid="missing-keywords-badge-group">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-warning" />
            <h3 className="font-semibold">{gaps?.items?.length ? "Job requirements your CV doesn't show" : "Missing keywords to add"}</h3>
          </div>
          {gaps?.items?.length > 0 && (
            <p className="text-xs text-faint mb-3">
              Review these in the <button onClick={reviewGaps} data-testid="missing-open-evidence" className="text-info hover:underline">Evidence Checker</button>. Nothing is added unless you confirm it.
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {(app.missing_keywords || []).map((k) => {
              const it = (gaps?.items || []).find((i) => i.skill.toLowerCase() === k.toLowerCase());
              const cls = !it ? "border-warning/30 bg-warning-soft text-warning"
                : it.status === "confirmed" ? "border-warning/30 bg-warning-soft text-warning"
                : it.status === "declined" ? "border-line-strong text-faint line-through"
                : "border-info/30 bg-info-soft text-info";
              return <span key={k} title={it ? it.status.replace("_", " ") : ""} className={`font-mono2 text-xs px-2.5 py-1 rounded-full border ${cls}`}>{it?.status === "confirmed" ? "✓ " : ""}{k}</span>;
            })}
            {(app.missing_keywords || []).length === 0 && <span className="text-sm text-faint">Nothing critical missing 🎯</span>}
          </div>
        </div>
      </div>

      <ToolsMatch app={app} testid="result-tools-match" />
      {app.keywords_added?.length > 0 && (
        <div className="rounded-xl border border-line border-l-[3px] border-l-brand bg-surface p-5" data-testid="keywords-added-group">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-brand" />
            <h3 className="font-semibold">Keywords woven into your CV</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {app.keywords_added.map((k) => (
              <span key={k} className="font-mono2 text-xs px-2.5 py-1 rounded-full border border-brand/40 bg-brand-soft text-brand">+ {k}</span>
            ))}
          </div>
        </div>
      )}

      {app.recommendations?.length > 0 && (
        <div className="rounded-xl border border-line bg-surface p-5">
          <h3 className="font-semibold mb-3">Optimizations applied to your CV</h3>
          <ul className="space-y-2">
            {app.recommendations.map((r, i) => {
              const isGap = /^gap:/i.test(r);
              return (
                <li key={i} className="flex gap-2.5 text-sm text-body">
                  <span className={`mt-0.5 ${isGap ? "text-warning" : "text-brand"}`}>{isGap ? "⚠" : "✓"}</span>
                  {r}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Gap coaching */}
      {gapCount > 0 && (
        <div className="rounded-xl border border-line border-l-[3px] border-l-kesh bg-surface p-5" data-testid="gap-coaching-card">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Lightbulb className="w-4 h-4 text-kesh" />
              <h3 className="font-semibold">Close your gaps — kind, doable tips</h3>
            </div>
            {!coaching && (
              <button
                data-testid="gap-coaching-button"
                onClick={loadCoaching}
                disabled={loadingCoach}
                className="gi-btn gi-btn-kesh gi-btn-sm"
              >
                {loadingCoach ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lightbulb className="w-4 h-4" />} Get tips
              </button>
            )}
          </div>
          {coaching && coaching.length > 0 && (
            <ul className="mt-4 space-y-3" data-testid="gap-coaching-list">
              {coaching.map((c, i) => (
                <li key={i} className="rounded-lg border border-line bg-canvas p-3">
                  <div className="text-xs font-mono2 uppercase tracking-widest text-warning mb-1">{c.gap}</div>
                  <p className="text-sm text-body leading-relaxed">{c.suggestion}</p>
                </li>
              ))}
            </ul>
          )}
          {coaching && coaching.length === 0 && (
            <p className="text-sm text-body mt-3">No gaps to coach — you're looking strong! 🎯</p>
          )}
        </div>
      )}

      {/* CV preview / editor */}
      <div className="rounded-xl border border-line bg-surface p-5">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-brand" />
            <h3 className="font-semibold">Tailored CV {editing ? "— editing" : "preview"}</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {editing ? (
              <>
                <button
                  data-testid="save-cv-edits-button"
                  onClick={saveEdit}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-brand text-onbrand text-sm font-semibold hover:bg-brand-hover transition-colors duration-200 disabled:opacity-60"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
                </button>
                <button
                  data-testid="cancel-cv-edits-button"
                  onClick={cancelEdit}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl border border-line text-ink text-sm font-medium hover:bg-subtle transition-colors duration-200"
                >
                  <X className="w-4 h-4" /> Cancel
                </button>
              </>
            ) : (
              <>
                <div className="flex items-center gap-1 rounded-full border border-line bg-canvas p-1" data-testid="pdf-template-toggle">
                  {[["classic", "Classic"], ["modern", "Modern"]].map(([id, label]) => (
                    <button
                      key={id}
                      data-testid={`template-${id}`}
                      onClick={() => setTemplate(id)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
 template === id ? "bg-brand text-onbrand" : "text-body hover:text-ink"
 }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <button
                  data-testid="edit-cv-button"
                  onClick={startEdit}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl border border-brand/40 text-brand text-sm font-medium hover:bg-brand-soft transition-colors duration-200"
                >
                  <Pencil className="w-4 h-4" /> Edit bullets
                </button>
                <button
                  data-testid="download-cv-pdf-button"
                  onClick={() => doDownload("cv")}
                  disabled={dl === "cv"}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-brand text-onbrand text-sm font-semibold hover:bg-brand-hover transition-colors duration-200 disabled:opacity-60"
                >
                  {dl === "cv" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} CV PDF
                </button>
                <button
                  data-testid="download-cv-docx-button"
                  onClick={() => doDownload("cv-docx")}
                  disabled={dl === "cv-docx"}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl border border-line text-ink text-sm font-medium hover:bg-subtle transition-colors duration-200 disabled:opacity-60"
                >
                  {dl === "cv-docx" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} CV Word
                </button>
              </>
            )}
          </div>
        </div>

        {editing ? (
          <div className="space-y-5" data-testid="cv-editor">
            <div>
              <label className="text-xs font-mono2 uppercase tracking-widest text-faint">Professional summary</label>
              <textarea
                data-testid="edit-summary-input"
                value={view.summary || ""}
                onChange={(e) => setDraft((d) => ({ ...d, summary: e.target.value }))}
                rows={3}
                className="mt-2 w-full bg-canvas border border-line-strong rounded-lg px-4 py-2.5 text-sm text-ink focus:outline-none focus:border-brand transition-colors resize-y"
              />
            </div>
            {(view.experience || []).map((e, ei) => (
              <div key={ei} className="rounded-lg border border-line bg-canvas p-4">
                <div className="text-sm font-semibold text-ink mb-2">{e.role} — {e.company}</div>
                <div className="space-y-2">
                  {(e.bullets || []).map((b, bi) => (
                    <div key={bi} className="flex gap-2 items-start">
                      <textarea
                        data-testid={`edit-bullet-${ei}-${bi}`}
                        value={b}
                        onChange={(ev) => setBullet(ei, bi, ev.target.value)}
                        rows={2}
                        className="flex-1 bg-surface border border-line-strong rounded-lg px-3 py-2 text-sm text-ink focus:outline-none focus:border-brand transition-colors resize-y"
                      />
                      <button
                        data-testid={`remove-bullet-${ei}-${bi}`}
                        onClick={() => removeBullet(ei, bi)}
                        className="mt-1 p-2 rounded-lg text-faint hover:text-danger hover:bg-subtle transition-colors"
                        title="Remove bullet"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    data-testid={`add-bullet-${ei}`}
                    onClick={() => addBullet(ei)}
                    className="flex items-center gap-1.5 text-sm text-brand hover:text-brand transition-colors"
                  >
                    <Plus className="w-4 h-4" /> Add bullet
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-lg bg-white text-ink p-5 sm:p-8 max-h-[520px] overflow-y-auto" data-testid="cv-preview-paper">
            <h1 className="text-2xl font-bold text-ink">{cv.name}</h1>
            <p className="text-brand-text font-medium">{cv.title}</p>
            <p className="text-xs text-faint mt-1">
              {[cv.contact?.email, cv.contact?.phone, cv.contact?.location, cv.contact?.linkedin].filter(Boolean).join("  |  ")}
            </p>
            {cv.summary && (
              <Section title="Professional Summary"><p className="text-sm leading-relaxed">{cv.summary}</p></Section>
            )}
            {cv.skills?.length > 0 && (
              <Section title="Core Skills"><p className="text-sm">{cv.skills.join(" • ")}</p></Section>
            )}
            {cv.experience?.length > 0 && (
              <Section title="Professional Experience">
                {cv.experience.map((e, i) => (
                  <div key={i} className="mb-3">
                    <div className="flex justify-between items-baseline">
                      <span className="font-semibold text-sm text-ink">{e.role} — {e.company}</span>
                      <span className="text-xs text-faint">{e.dates}</span>
                    </div>
                    <ul className="list-disc ml-5 mt-1 space-y-0.5">
                      {(e.bullets || []).map((b, j) => <li key={j} className="text-sm leading-snug">{b}</li>)}
                    </ul>
                  </div>
                ))}
              </Section>
            )}
            {cv.education?.length > 0 && (
              <Section title="Education">
                {cv.education.map((ed, i) => (
                  <p key={i} className="text-sm"><span className="font-semibold">{ed.degree}</span>, {ed.institution} {ed.dates && `(${ed.dates})`}</p>
                ))}
              </Section>
            )}
            {cv.certifications?.length > 0 && (
              <Section title="Certifications"><p className="text-sm">{cv.certifications.join(" • ")}</p></Section>
            )}
          </div>
        )}
      </div>

      {/* Cover letter */}
      {app.cover_letter && (
        <div className="rounded-xl border border-line bg-surface p-5">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <h3 className="font-semibold">Cover letter</h3>
            <div className="flex gap-2">
              <button
                data-testid="download-cover-letter-button"
                onClick={() => doDownload("cl")}
                disabled={dl === "cl"}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-brand text-onbrand text-sm font-semibold hover:bg-brand-hover transition-colors duration-200 disabled:opacity-60"
              >
                {dl === "cl" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} PDF
              </button>
              <button
                data-testid="download-cover-letter-docx-button"
                onClick={() => doDownload("cl-docx")}
                disabled={dl === "cl-docx"}
                className="flex items-center gap-2 px-4 py-2 rounded-xl border border-line text-ink text-sm font-medium hover:bg-subtle transition-colors duration-200 disabled:opacity-60"
              >
                {dl === "cl-docx" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Word
              </button>
            </div>
          </div>
          <div className="rounded-lg bg-canvas border border-line p-5 max-h-72 overflow-y-auto">
            <p className="text-sm text-body whitespace-pre-wrap leading-relaxed">{app.cover_letter}</p>
          </div>
        </div>
      )}

      {/* Email recruiter */}
      <div className="rounded-xl border border-line bg-surface p-5">
        <div className="flex items-center gap-2 mb-1">
          <Mail className="w-4 h-4 text-brand" />
          <h3 className="font-semibold">Email this application to a recruiter</h3>
        </div>
        <p className="text-sm text-faint mb-4">We'll send your tailored CV + cover letter. Replies go straight to your inbox.</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            data-testid="recruiter-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="recruiter@company.com"
            className="flex-1 bg-canvas border border-line-strong rounded-lg px-4 py-2.5 text-sm text-ink placeholder:text-faint focus:outline-none focus:border-brand transition-colors"
          />
          <button
            data-testid="send-cv-email-button"
            onClick={() => sendEmail()}
            disabled={sending}
            className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-brand text-onbrand text-sm font-semibold hover:bg-brand-hover transition-colors duration-200 disabled:opacity-60"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {sent ? "Send again" : "Send application"}
          </button>
        </div>
        {sent && (
          <div className="mt-3 flex items-center gap-2 text-sm text-brand">
            <CheckCircle2 className="w-4 h-4" /> Application email sent.
          </div>
        )}
      </div>
      {/* Private notes */}
      <div className="rounded-xl border border-line bg-surface p-5" data-testid="application-notes">
        <div className="flex items-center gap-2 mb-1">
          <StickyNote className="w-4 h-4 text-brand" />
          <h3 className="font-semibold">Private notes</h3>
        </div>
        <p className="text-sm text-faint mb-4">Only you can see these — track the recruiter, follow-up date and anything else.</p>
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-xs font-mono2 uppercase tracking-widest text-faint">Recruiter / contact</label>
            <input
              data-testid="notes-recruiter-input"
              value={notes.recruiter_name}
              onChange={(e) => setNotes((n) => ({ ...n, recruiter_name: e.target.value }))}
              placeholder="e.g. Jordan at TechFlow"
              className="mt-1.5 w-full bg-canvas border border-line-strong rounded-lg px-3 py-2 text-sm text-ink placeholder:text-faint focus:outline-none focus:border-brand transition-colors"
            />
          </div>
          <div>
            <label className="text-xs font-mono2 uppercase tracking-widest text-faint">Follow-up date</label>
            <input
              data-testid="notes-followup-input"
              type="date"
              value={notes.follow_up_date}
              onChange={(e) => setNotes((n) => ({ ...n, follow_up_date: e.target.value }))}
              className="mt-1.5 w-full bg-canvas border border-line-strong rounded-lg px-3 py-2 text-sm text-ink focus:outline-none focus:border-brand transition-colors"
            />
          </div>
        </div>
        <textarea
          data-testid="notes-text-input"
          value={notes.notes}
          onChange={(e) => setNotes((n) => ({ ...n, notes: e.target.value }))}
          rows={3}
          placeholder="Interview stages, salary discussed, things to remember…"
          className="w-full bg-canvas border border-line-strong rounded-lg px-3 py-2 text-sm text-ink placeholder:text-faint focus:outline-none focus:border-brand transition-colors resize-y"
        />
        <button
          data-testid="save-notes-button"
          onClick={saveNotes}
          disabled={savingNotes}
          className="mt-3 flex items-center gap-2 px-4 py-2 rounded-xl bg-brand text-onbrand text-sm font-semibold hover:bg-brand-hover transition-colors duration-200 disabled:opacity-60"
        >
          {savingNotes ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save notes
        </button>
      </div>

      <ShareWinModal open={shareOpen} onOpenChange={setShareOpen} score={atsScore} />
      <AlertDialog open={confirmSend} onOpenChange={setConfirmSend}>
        <AlertDialogContent data-testid="skill-gaps-send-confirm" className="bg-surface border-line text-ink">
          <AlertDialogHeader>
            <AlertDialogTitle>Before you send</AlertDialogTitle>
            <AlertDialogDescription className="text-body">
              Your CV still lists {unconfirmed.length} skill{unconfirmed.length === 1 ? "" : "s"} that an earlier version added automatically and you haven't confirmed:{" "}
              <span className="text-ink font-medium">{unconfirmed.map((i) => i.skill).join(", ")}</span>. The recruiter may ask about each one. Review them first, or send only if you can back them up.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="skill-gaps-send-review" className="bg-transparent border-line text-ink hover:bg-subtle hover:text-ink"
              onClick={reviewGaps}>
              Review skills
            </AlertDialogCancel>
            <AlertDialogAction data-testid="skill-gaps-send-anyway" className="bg-brand text-onbrand hover:bg-brand-hover" onClick={() => sendEmail(true)}>
              I can back them up, send
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-4">
      <h2 className="text-xs font-bold uppercase tracking-wider text-brand-text border-b border-line pb-1 mb-2">{title}</h2>
      {children}
    </div>
  );
}
